/**
 * Import-actuals modal. Calls the live preview/import endpoints and surfaces
 * resolved entries + per-row errors. The modal does not mutate App-level
 * `actuals` directly — on a successful commit it asks App to refetch the
 * transitional global GET, so all consumers see the same authoritative state.
 *
 * "Download example" generates a personalised CSV at click time:
 *   - one row per current-user member project, with `(p.code, name, prevYM, 0)`
 *   - if the user has no member projects, a single placeholder row
 */
import { type FC, useMemo, useState } from "react";

import type { Person, Project } from "@shared/types";
import { downloadCsv, previousYmKey } from "./data";
import { apiFetch } from "./lib/api";
import { useC } from "./theme";
import { Btn, Modal } from "./ui";

export interface ImportActualsModalProps {
  projects: Project[];
  people: Person[];
  currentUserId: number | null;
  onCommitted: () => void;
  onClose: () => void;
}

const HEADER_WORDS = ["project code", "user", "email", "month", "hours", "name", "person"];

export function looksLikeHeader(line: string): boolean {
  const lc = line.toLowerCase();
  if (!HEADER_WORDS.some((w) => lc.includes(w))) return false;
  if (line.includes("@")) return false;
  for (const cell of line.split(",")) {
    const t = cell.trim();
    if (t === "") continue;
    if (Number.isFinite(Number(t))) return false;
  }
  return true;
}

export function countDataRows(text: string): number {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  if (lines.length === 0) return 0;
  if (looksLikeHeader(lines[0] ?? "")) return lines.length - 1;
  return lines.length;
}

interface PreviewEntry {
  projectId: number;
  projectCode: string;
  projectName: string;
  personId: number;
  personName: string;
  ymKey: string;
  hours: number;
}

interface PreviewState {
  ok: boolean;
  err?: string;
  entries: PreviewEntry[];
  errors: string[];
  imported?: number;
  skipped?: number;
}

export const ImportActualsModal: FC<ImportActualsModalProps> = ({
  projects,
  people,
  currentUserId,
  onCommitted,
  onClose,
}) => {
  const { C } = useC();
  const [text, setText] = useState(
    `Project Code,Email,Month,Hours
MSA2,tonis@dolmit.com,2026-05,140
MSA2,anna@dolmit.com,2026-05,132
HTV-III,liis@dolmit.com,2026-05,48
HTV-III,peeter@dolmit.com,2026-05,96`,
  );
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [busy, setBusy] = useState(false);

  async function callPreview() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiFetch("/api/actuals/preview", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ csv: text }),
      });
      if (res.status === 422) {
        const body = (await res.json()) as { message?: string };
        setPreview({ ok: false, err: body.message ?? "CSV rejected", entries: [], errors: [] });
        return;
      }
      if (!res.ok) {
        setPreview({ ok: false, err: `Server error (${res.status})`, entries: [], errors: [] });
        return;
      }
      const body = (await res.json()) as { entries: PreviewEntry[]; errors: string[] };
      setPreview({ ok: true, entries: body.entries, errors: body.errors });
    } finally {
      setBusy(false);
    }
  }

  async function callImport() {
    if (busy) return;
    setBusy(true);
    try {
      const res = await apiFetch("/api/actuals/import", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ csv: text }),
      });
      if (res.status === 422) {
        const body = (await res.json()) as { message?: string };
        setPreview({ ok: false, err: body.message ?? "CSV rejected", entries: [], errors: [] });
        return;
      }
      if (!res.ok) {
        setPreview({ ok: false, err: `Server error (${res.status})`, entries: [], errors: [] });
        return;
      }
      const body = (await res.json()) as {
        imported: number;
        skipped: number;
        errors: string[];
      };
      setPreview({
        ok: true,
        entries: [],
        errors: body.errors,
        imported: body.imported,
        skipped: body.skipped,
      });
      // Skip the App-level refetch when no rows landed — the global state
      // hasn't changed and a no-op round-trip is just noise.
      if (body.imported > 0) onCommitted();
    } finally {
      setBusy(false);
    }
  }

  function downloadExample() {
    const me = currentUserId == null ? null : people.find((p) => p.id === currentUserId);
    const meEmail = me?.email ?? "EXAMPLE@example.com";
    const prevYm = previousYmKey(new Date());
    const myProjects =
      currentUserId == null
        ? []
        : projects.filter((p) => p.members.some((m) => m.pid === currentUserId));
    const rows: unknown[][] = [["Project Code", "Email", "Month", "Hours"]];
    if (myProjects.length === 0) {
      rows.push(["EXAMPLE", meEmail, prevYm, 0]);
    } else {
      for (const p of myProjects) {
        rows.push([p.code, meEmail, prevYm, 0]);
      }
    }
    downloadCsv("actuals-example.csv", rows);
  }

  const rowCount = useMemo(() => countDataRows(text), [text]);

  return (
    <Modal title="Import Actuals (CSV)" onClose={onClose} wide>
      <p
        style={{
          fontSize: 12.5,
          color: C.slateMut,
          marginTop: 0,
          marginBottom: 12,
          lineHeight: 1.5,
        }}
      >
        Paste a CSV or upload a file exported from your time-tracking system. Two formats are
        accepted: native (
        <strong style={{ color: C.navy }}>Project Code, User or Email, Month, Hours</strong>) and
        Atlassian Tempo timesheets. Imported values overwrite existing actuals for the same (project
        · person · month).
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
        <label
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            borderRadius: 6,
            border: `1px solid ${C.line}`,
            background: C.surface,
            fontSize: 12,
            fontWeight: 600,
            color: C.navy,
            cursor: "pointer",
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          }}
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <title>Upload</title>
            <path
              d="M7 13V5m0 0l-3 3m3-3l3 3M2 2h10"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
            />
          </svg>
          Upload file
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const reader = new FileReader();
              reader.onload = () => setText(String(reader.result || ""));
              reader.readAsText(f);
            }}
            style={{ display: "none" }}
          />
        </label>
        <Btn small onClick={() => void callPreview()} disabled={busy}>
          Preview
        </Btn>
        <Btn small onClick={downloadExample}>
          Download example
        </Btn>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          fontSize: 11,
          color: C.slateMut,
          marginBottom: 4,
        }}
      >
        <span>{rowCount} rows</span>
      </div>
      <textarea
        aria-label="CSV input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{
          width: "100%",
          minHeight: 180,
          padding: 10,
          borderRadius: 8,
          border: `1px solid ${C.line}`,
          fontSize: 12,
          fontFamily: "ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",
          color: C.navy,
          background: C.canvas,
          outline: "none",
          resize: "vertical",
          lineHeight: 1.5,
        }}
      />

      {preview && (
        <div
          style={{
            marginTop: 12,
            background: preview.ok ? C.blueTint1 : C.rBg,
            border: `1px solid ${preview.ok ? C.blueTint4 : C.rBd}`,
            borderRadius: 8,
            padding: 12,
          }}
        >
          {!preview.ok && (
            <div style={{ fontSize: 12.5, color: C.r, fontWeight: 600 }}>⚠ {preview.err}</div>
          )}
          {preview.ok && (
            <>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: preview.imported !== undefined ? C.g : C.blue,
                  marginBottom: 6,
                }}
              >
                {preview.imported !== undefined
                  ? `✓ Imported ${preview.imported} entries`
                  : `${preview.entries.length} rows ready to import`}
                {preview.skipped !== undefined && preview.skipped > 0 && (
                  <span style={{ color: C.y, marginLeft: 8, fontSize: 11 }}>
                    · {preview.skipped} skipped
                  </span>
                )}
                {preview.imported === undefined && preview.errors.length > 0 && (
                  <span style={{ color: C.y, marginLeft: 8, fontSize: 11 }}>
                    · {preview.errors.length} skipped
                  </span>
                )}
              </div>
              {preview.entries.length > 0 && (
                <div
                  style={{
                    maxHeight: 140,
                    overflow: "auto",
                    fontSize: 11,
                    fontFamily: "ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",
                    color: C.slate,
                  }}
                >
                  {preview.entries.slice(0, 20).map((e, i) => (
                    <div key={`${e.projectId}-${e.personId}-${e.ymKey}-${i}`}>
                      {e.projectCode} · {e.personName} · {e.ymKey} · {e.hours}h
                    </div>
                  ))}
                  {preview.entries.length > 20 && (
                    <div style={{ color: C.slateMut }}>…and {preview.entries.length - 20} more</div>
                  )}
                </div>
              )}
              {preview.errors.length > 0 && (
                <div
                  style={{
                    marginTop: 6,
                    maxHeight: 80,
                    overflow: "auto",
                    fontSize: 11,
                    color: C.y,
                  }}
                >
                  {preview.errors.slice(0, 6).map((e) => (
                    <div key={e}>{e}</div>
                  ))}
                  {preview.errors.length > 6 && (
                    <div style={{ color: C.slateMut }}>…and {preview.errors.length - 6} more</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "flex-end",
          marginTop: 14,
          paddingTop: 14,
          borderTop: `1px solid ${C.lineSoft}`,
        }}
      >
        <Btn onClick={onClose}>Close</Btn>
        <Btn primary onClick={() => void callImport()} disabled={busy}>
          Import
        </Btn>
      </div>
    </Modal>
  );
};
